import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {
  type InfiniteData,
  type QueryKey,
  useSuspenseInfiniteQuery,
  type UseSuspenseInfiniteQueryResult,
} from '@github-ui/react-query'
import {reactFetch} from '@github-ui/verified-fetch'
import type {ReviewSummariesPayload} from '../payloads/review-summaries'
import {useReviewSummariesActions} from '../../stores/selectors'
import {parseLinkHeader} from '../../utils/parse-link-header'

export function reviewSummariesQueryKey(pathName: string): QueryKey {
  return [PageData.reviewSummaryComments, pathName]
}

export type ReviewSummariesPage = {
  data: ReviewSummariesPayload
  nextUrl: string | null
}

export type ReviewSummariesPaginatedData = {
  pages: ReviewSummariesPage[]
  pageParams: string[]
}

export function reviewSummariesApiUrl(pathName: string): string {
  return `${pathName}/page_data/${PageData.reviewSummaryComments}`
}

export function useInfiniteReviewSummariesPageData({
  pathName,
}: {
  pathName: string
}): UseSuspenseInfiniteQueryResult<InfiniteData<ReviewSummariesPage>> {
  const {addReviewSummaries} = useReviewSummariesActions()

  return useSuspenseInfiniteQuery<ReviewSummariesPage, Error, InfiniteData<ReviewSummariesPage>, QueryKey, string>({
    queryKey: reviewSummariesQueryKey(pathName),
    initialPageParam: reviewSummariesApiUrl(pathName),
    queryFn: async ({pageParam}: {pageParam: string}): Promise<ReviewSummariesPage> => {
      const result = await reactFetch(pageParam)
      if (!result.ok) throw new Error(`Failed to fetch review summaries from ${pageParam}: HTTP ${result.status}`)

      const json = await result.json()
      const linkHeader = result.headers.get('Link')
      const nextUrl = parseLinkHeader(linkHeader, 'next')
      const data = json as ReviewSummariesPayload

      addReviewSummaries(data)
      return {data, nextUrl}
    },
    getNextPageParam: (lastPage: ReviewSummariesPage) => {
      return lastPage.nextUrl ?? undefined
    },
    staleTime: Infinity,
  })
}
